import { quickPercents } from './discountForm';
import type { BookingDraft } from './draft';
import { depositMultiplierChoices, tokenPercents } from './takeBooking';

/**
 * The bag's three figures — discount, token, deposit — each given as a percentage or in rupees,
 * with quick choices and a box to type any figure (canvas "Booking flow", 2026-10-26). Nothing new is
 * stored: the draft already keeps each both ways —
 *
 * <ul>
 * <li>discount: `discountPct` or `discountAmount`, of the rent;</li>
 * <li>token: `tokenPercent` or `tokenOverride`, of the rent after discount;</li>
 * <li>deposit: `depositMultiplier` (150% is ×1.5) or `depositOverride`, of the rent before
 * discount.</li>
 * </ul>
 *
 * The figures themselves are still `draftMoney`'s; this only reads and writes the draft's fields.
 */

export type FigureMode = 'pct' | 'amount';

/** A share of a base as a percentage, to one decimal — what a box shows after switching to %. */
export function pctOf(amount: number, base: number): number {
  return base > 0 ? Math.round((amount / base) * 1000) / 10 : 0;
}

export const discountMode = (draft: BookingDraft): FigureMode =>
  draft.discountAmount !== null ? 'amount' : 'pct';

export const tokenMode = (draft: BookingDraft): FigureMode =>
  draft.tokenOverride !== null ? 'amount' : 'pct';

export const depositMode = (draft: BookingDraft): FigureMode =>
  draft.depositOverride !== null ? 'amount' : 'pct';

/** The figure the box holds, in its own mode — 5 for 5%, 500 for ₹500; null is an empty box. */
export function discountFigure(draft: BookingDraft): number | null {
  return discountMode(draft) === 'amount' ? draft.discountAmount : draft.discountPct;
}

export function tokenFigure(draft: BookingDraft): number {
  return tokenMode(draft) === 'amount' ? (draft.tokenOverride ?? 0) : draft.tokenPercent;
}

export function depositFigure(draft: BookingDraft): number {
  return depositMode(draft) === 'amount'
    ? (draft.depositOverride ?? 0)
    : Math.round(draft.depositMultiplier * 1000) / 10;
}

/** A discount given one way clears the other; nothing (or zero) is no discount at all. */
export function discountPatch(mode: FigureMode, value: number | null): Partial<BookingDraft> {
  const given = value === null || value <= 0 ? null : value;

  return mode === 'pct'
    ? { discountPct: given, discountAmount: null }
    : { discountPct: null, discountAmount: given };
}

export function tokenPatch(mode: FigureMode, value: number | null): Partial<BookingDraft> {
  return mode === 'pct'
    ? { tokenPercent: Math.max(0, value ?? 0), tokenOverride: null }
    : { tokenOverride: Math.max(0, value ?? 0) };
}

/** 150% of the rent is the multiple 1.5; a rupee figure is an override, kept with its reason. */
export function depositPatch(mode: FigureMode, value: number | null): Partial<BookingDraft> {
  return mode === 'pct'
    ? { depositMultiplier: Math.max(0, value ?? 0) / 100, depositOverride: null }
    : { depositOverride: Math.max(0, value ?? 0) };
}

/** Switching keeps the money: 5% of ₹10,000 becomes ₹500, and ₹500 becomes 5% again. */
export function switchedFigure(to: FigureMode, money: number, base: number): number {
  return to === 'amount' ? Math.round(money) : pctOf(money, base);
}

/** The quick percentages: none, then the steps within this clerk's limit. */
export const discountPctChoices = (ceilingPct: number): number[] => [
  0,
  ...quickPercents(ceilingPct),
];

export const tokenPctChoices: readonly number[] = tokenPercents;

/** The shop's multiples as percentages — 100%, 150%, 200%, 300%, and the shop's own. */
export const depositPctChoices = (shopMultiplier: number): number[] =>
  depositMultiplierChoices(shopMultiplier).map((multiple) => Math.round(multiple * 1000) / 10);
